/**
 * SpeakerMountingSelector Component
 *
 * This component provides a dropdown to select speaker mounting type (ceiling, pendant, or wall).
 * It is hidden when a conferencing bar is selected.
 */
import React from 'react';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import Select from '../atoms/Select';
import InputLabel from '../atoms/InputLabel';
import ReducersIndex from '../../reduxModules/ducks';
import { getAreaProperty } from 'reduxModules/selectors/containerSelectors';
import { SpeakerMountingType } from '../../types/redux/room';

interface SpeakerMountingSelectorProps {
  show?: boolean;
  disabled?: boolean;
  label?: string;
}

const SpeakerMountingSelector: React.FC<SpeakerMountingSelectorProps> = ({ show = true, disabled = false, label }) => {
  const dispatch = useDispatch();

  // Get current mounting type from state
  const currentMountingType = useSelector(state =>
    getAreaProperty(state, 'speakers.mountingType') || 'ceiling'
  ) as SpeakerMountingType;

  // Check if conferencing bar is selected
  const hasBar = useSelector(state => getAreaProperty(state, 'hasBar'));
  const microphoneModel = useSelector(state => getAreaProperty(state, 'microphones.model'));
  const isBarSelected = hasBar || (microphoneModel && microphoneModel.includes('bar'));

  // Hide selector if conferencing bar is selected
  if (isBarSelected || !show) {
    return null;
  }

  const mountingTypes: SpeakerMountingType[] = ['ceiling', 'pendant', 'wall'];

  const handleChange = (event: any) => {
    const newMountingType = event.target.value as SpeakerMountingType;
    console.log('🔄 SpeakerMountingSelector: Changing mounting type to', newMountingType);
    dispatch((ReducersIndex.container as any).setSpeakerMountingType({ mountingType: newMountingType }));
  };

  return (
    <Box className="d-flex flex-column" sx={{ width: '100%' }}>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        value={currentMountingType}
        items={mountingTypes}
        onChange={handleChange}
        disabled={disabled}
        placeHolder="Select mounting type"
        bubbles={{
          ceiling: 'Ceiling',
          pendant: 'Pendant',
          wall: 'Wall'
        }}
      />
    </Box>
  );
};

export default SpeakerMountingSelector;